import React, { useMemo, useRef } from "react";

import { SelectionRenderer } from "@src/custom_renderer";
import ActionsRenderer from "@src/custom_renderer/actions_renderer/actions_renderer";
import { mapToTreeData } from "@src/features/table_filtering/utils/filter_utils";
import {
  useGetCountriesQuery,
  useUpdateCountryMutation,
} from "@src/services/slices/countriesApi";
import { Country, Region } from "@src/types";
import { GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { App } from "antd";
import _ from "lodash";

import { getGridOptions } from "../../utils/region_assignment_management_grid_options";
import { onChange } from "../../utils/region_assignment_utils";
import "./region_assignment_management.less";
import { RegionAssignmentFilter } from "../region_assignment_filter";

interface RegionAssignmentManagementProps {
  regionsData: Region[];
}

const RegionAssignmentManagement: React.FC<RegionAssignmentManagementProps> = ({
  regionsData,
}) => {
  const { data: countriesData, isLoading } = useGetCountriesQuery();
  const gridApi = useRef(null);

  const { message } = App.useApp();

  const [updateCountry] = useUpdateCountryMutation();

  const context = useMemo(
    () => ({
      regions: regionsData,
      countries: countriesData,
    }),
    [regionsData, countriesData]
  );

  function onGridReady(params: GridReadyEvent): void {
    gridApi.current = params.api;
    gridApi.current.sizeColumnsToFit();
  }

  /**
   * onClick function of the edit button
   * @param {ICellRendererParams} props - ag-grid props
   */
  function onEdit(props: ICellRendererParams): void {
    props.data.isEditActive = true;
    props.api.refreshCells({ rowNodes: [props.node], force: true });
  }

  /**
   * onClick function of the cancel button
   * @param {ICellRendererParams} props  - ag-grid props
   */
  function onCancel(props: ICellRendererParams): void {
    props.data.isEditActive = false;
    props.api.redrawRows({ rowNodes: [props.node] });
  }

  /**
   * onClick function of the save button
   * @param {ICellRendererParams} props - ag-grid props
   */
  async function handleSave(props: ICellRendererParams): Promise<void> {
    await editCountry(props);
  }

  /**
   * Edit country
   * @param {ICellRendererParams} props - ag-grid props
   */
  async function editCountry(props: ICellRendererParams) {
    let country: Country = _.cloneDeep(props.data);

    if (props?.data?.updatedData) {
      country = { ...country, ...props.data.updatedData };
    }

    updateCountry(country)
      .unwrap()
      .then(() => {
        updateNodeData(props);
        message.success(`Successfully updated region`);
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {});
  }

  /**
   * Update node data
   * @param {ICellRendererParams} props - ag-grid props
   *
   */
  function updateNodeData(props: ICellRendererParams): void {
    if (props?.data?.updatedData) {
      Object.keys(props.data.updatedData).forEach((key: string) => {
        props.node.setDataValue(key, _.cloneDeep(props.data.updatedData[key]));
      });
      delete props.data.updatedData;
    }
    delete props.data.isEditActive;

    props.api.refreshCells({ rowNodes: [props.node], force: true });
  }

  //----------------------
  // AG-GRID FUNCTIONS
  //----------------------

  const frameworkComponents = {
    selectionRenderer: (props: ICellRendererParams) => {
      return (
        <SelectionRenderer
          props={props}
          allowClear
          dataToExtractOptions={props.context.regions}
          propertyForOptionLabels="regionName"
          propertyForOptionValues="regionName"
          defaultValue={props?.data?.newRow ? null : props?.data?.regionName}
          onChangeCallback={(selectedRegionName: string) =>
            onChange(selectedRegionName, props)
          }
          placeholder="Select Region"
          disabled={!props?.data.isEditActive}
        />
      );
    },

    actionsRenderer: (props: ICellRendererParams) => {
      return (
        <ActionsRenderer
          props={props}
          onSave={handleSave}
          onEdit={onEdit}
          onCancel={onCancel}
        />
      );
    },
  };

  return (
    <div className="region-assignment-management">
      <div className="top-section" data-testid="top-section">
        <RegionAssignmentFilter
          gridApi={gridApi.current}
          regions={mapToTreeData(context?.regions, "regionName")}
          countries={mapToTreeData(context?.countries, "countryName")}
          disabled={isLoading}
        />
      </div>
      <div data-testid="region-settings-table">
        <AgGridReact
          rowData={_.cloneDeep(countriesData)}
          components={frameworkComponents}
          gridOptions={getGridOptions()}
          onGridReady={onGridReady}
          context={context}
          onGridSizeChanged={(props) => props.api.sizeColumnsToFit()}
        />
      </div>
    </div>
  );
};

export default RegionAssignmentManagement;
